// A failed tax calculation used to be invisible. The wizard's baseline and
// strategy recalcs run in useEffect, not a react-query mutation, so App.tsx's
// global MutationCache catch never saw them, and both call sites swallowed the
// error with a bare catch. The spinner stopped, the panel stayed blank, and
// nothing reached the error dashboard (a pre-2024 1040 import, 2026-08-27, cost
// an afternoon to diagnose for exactly this reason).
//
// reportCalcFailure() is the one place those failures go: logged through the
// session-free error logger, and shown to the advisor as a single toast. The
// stable toast id matters — recalcs fire on every debounced edit, so a failing
// input would otherwise stack a new toast per keystroke.

import { toast } from 'sonner';
import { logClientError } from '@/lib/errorLogger';
import { sanitizeError } from '@/lib/errorMessages';

export const CALC_FAILED_TOAST_ID = 'calc-failed';

function rawMessage(err: unknown): string {
  if (err instanceof Error) return err.message;
  if (typeof err === 'string') return err;
  return String((err as { message?: unknown } | null)?.message ?? err ?? '');
}

/** What the advisor reads. Never the raw server text. */
export function calcFailureMessage(err: unknown): string {
  const raw = rawMessage(err);
  // calculate-tax's schema rejection (supabase/functions/calculate-tax/index.ts).
  if (/invalid input/i.test(raw)) {
    return 'One of the inputs is outside what the tax engine accepts. Check the numbers and the tax year.';
  }
  if (/unauthorized/i.test(raw)) return 'Your session has expired. Please sign in again.';
  const friendly = sanitizeError(err);
  return friendly === 'Something went wrong. Please try again.'
    ? "The tax numbers couldn't be calculated. Try again, and contact support if it keeps happening."
    : friendly;
}

export function reportCalcFailure(err: unknown, where: 'baseline' | 'strategy'): void {
  void logClientError({
    error_message: `calculate-tax (${where}): ${rawMessage(err) || 'unknown error'}`,
    query_name: 'calculate-tax',
    error_type: 'rpc',
  });
  toast.error("Couldn't calculate taxes", {
    id: CALC_FAILED_TOAST_ID,
    description: calcFailureMessage(err),
    duration: 10_000,
  });
}
